// Auto-force-refresh installed PWAs whenever the loaded bundle is on an
// older APP_VERSION than the backend advertises.
//
// v1.28.45 — Behaviour split:
//   * `checkForUpdateAndReload()` no longer force-reloads instantly. It only
//     records that a mismatch is detected so `<UpdateBanner>` can show the
//     yellow "Refreshing in 60 s…" bar and let the user tap Refresh Now,
//     Dismiss, or wait out the countdown. Immediate silent reloads used to
//     race the banner render, which is why users never saw it.
//   * `installSwUpdateListener()` still watches for `controllerchange` /
//     `SW_UPDATED` and now forwards those to UpdateBanner via a
//     `window.dispatchEvent(new CustomEvent("micm:update-detected"))` rather
//     than reloading. The banner takes over from there.
import { APP_VERSION } from "@/lib/version";

const UPDATE_EVENT = "micm:update-detected";

function notifyBanner(reason, version) {
    if (typeof window === "undefined") return;
    try {
        window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { reason, version } }));
    } catch (_e) { /* older browsers */ }
}

export function installSwUpdateListener() {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker.addEventListener("controllerchange", () => notifyBanner("controllerchange"));
    navigator.serviceWorker.addEventListener("message", (ev) => {
        if (ev?.data?.type === "SW_UPDATED") notifyBanner(`SW_UPDATED`, ev.data.version);
    });
    // Poke every registration to check for updates on tab wake — the browser
    // will fire `controllerchange` if there really is a fresh SW waiting.
    navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => { try { r.update(); } catch (_e) {} });
    }).catch(() => {});
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        navigator.serviceWorker.getRegistrations().then((regs) => {
            regs.forEach((r) => { try { r.update(); } catch (_e) {} });
        }).catch(() => {});
    });
}

export async function checkForUpdateAndReload() {
    try {
        const res = await fetch("/api/version", { cache: "no-store" });
        if (!res.ok) return;
        const j = await res.json();
        if (!j?.version) return;
        if (j.version === APP_VERSION) return;
        // Do NOT auto-reload here — surface it to the banner instead so the
        // user sees the yellow "Refreshing in 60 s…" strip.
        // eslint-disable-next-line no-console
        console.info(`[MICM] update detected: running=${APP_VERSION} deployed=${j.version}`);
        notifyBanner("version-mismatch", j.version);
    } catch { /* offline / non-fatal */ }
}
